import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { FilterAst, SortSpec } from '@castinflow/shared';
import { useSession } from './session';
import { clearMetadataMemory } from './metadata';

/**
 * Önbellek kapsamı (WP 2.8 §29): her sorgu anahtarı tenant + kullanıcı ile başlar. Böylece aynı sekmede başka kullanıcı
 * veya tenant oturumu açılsa bile önceki kullanıcının kayıtları, yetkileri veya metadata'sı yeniden kullanılmaz; ayrıca
 * kapsam değişince (çıkış, başka kullanıcı/tenant) bütün bellek önbelleği temizlenir (`useScopeGuard`).
 */
export interface Scope {
  tenantId: string;
  userId: string;
}

export function useScope(): Scope | null {
  const me = useSession((s) => s.me);
  return me ? { tenantId: me.tenant.id, userId: me.user.id } : null;
}

export function scopeKey(s: Scope | null): string {
  return s === null ? 'anon' : `${s.tenantId}:${s.userId}`;
}

const root = (s: Scope) => ['t', s.tenantId, 'u', s.userId] as const;

export interface ListQueryKey {
  listId: string | null;
  filter: FilterAst | null;
  sort: readonly SortSpec[] | null;
  columns: readonly string[];
  cursor: string | null;
}

/** Sorgu anahtarı fabrikası: bağlam (tenant, kullanıcı, tip, kimlik, liste, filtre, sıralama, cursor) anahtardadır. */
export const qk = {
  metadata: (s: Scope) => [...root(s), 'metadata'] as const,
  catalog: (s: Scope, type: string) => [...root(s), 'catalog', type] as const,
  lists: (s: Scope, type: string) => [...root(s), 'lists', type] as const,
  query: (s: Scope, type: string, k: ListQueryKey) => [...root(s), 'query', type, k] as const,
  total: (s: Scope, type: string, k: Pick<ListQueryKey, 'listId' | 'filter'>) =>
    [...root(s), 'total', type, k] as const,
  /** Tipin tüm liste/toplam sorguları (önek; kayıt oluşturma/silme/toplu işlem sonrası geçersiz kılma) */
  queriesOf: (s: Scope, type: string) => [...root(s), 'query', type] as const,
  totalsOf: (s: Scope, type: string) => [...root(s), 'total', type] as const,
  record: (s: Scope, type: string, id: string) => [...root(s), 'record', type, id] as const,
  acl: (s: Scope, type: string, id: string) => [...root(s), 'acl', type, id] as const,
  tags: (s: Scope, type: string, id: string) => [...root(s), 'tags', type, id] as const,
  tagOptions: (s: Scope, q: string) => [...root(s), 'tag-options', q] as const,
  reminders: (s: Scope, type: string, id: string) => [...root(s), 'reminders', type, id] as const,
  attachments: (s: Scope, type: string, id: string) =>
    [...root(s), 'attachments', type, id] as const,
  notifications: (s: Scope) => [...root(s), 'notifications'] as const,
  departments: (s: Scope) => [...root(s), 'departments'] as const,
  refs: (s: Scope, ids: string) => [...root(s), 'refs', ids] as const,
  // WP 2.9
  lookups: (s: Scope) => [...root(s), 'lookups'] as const,
  stats: (s: Scope, type: string, id: string) => [...root(s), 'stats', type, id] as const,
  activity: (s: Scope, type: string, id: string) => [...root(s), 'activity', type, id] as const,
  companyContacts: (s: Scope, id: string) => [...root(s), 'company-contacts', id] as const,
  similar: (s: Scope, type: string, id: string) => [...root(s), 'similar', type, id] as const,
  inbox: (s: Scope) => [...root(s), 'inbox'] as const,
  // WP 2.10
  productCategories: (s: Scope) => [...root(s), 'product-categories'] as const,
  priceCategories: (s: Scope) => [...root(s), 'price-categories'] as const,
  productSets: (s: Scope) => [...root(s), 'product-sets'] as const,
  productSettings: (s: Scope) => [...root(s), 'product-settings'] as const,
  productPrices: (s: Scope, id: string) => [...root(s), 'product-prices', id] as const,
  priceHistory: (s: Scope, id: string) => [...root(s), 'price-history', id] as const,
  stock: (s: Scope, id: string) => [...root(s), 'stock', id] as const,
  // M3: satış belgeleri
  salesDoc: (s: Scope, type: string, id: string) => [...root(s), 'sales-doc', type, id] as const,
  salesApprovals: (s: Scope) => [...root(s), 'sales-approvals'] as const,
  salesApprovalDecisions: (s: Scope) => [...root(s), 'sales-approval-decisions'] as const,
  opportunityStages: (s: Scope) => [...root(s), 'opportunity-stages'] as const,
  opportunityRelated: (s: Scope, id: string) => [...root(s), 'opportunity-related', id] as const,
  opportunityBoard: (s: Scope) => [...root(s), 'opportunity-board'] as const,
  salesRelated: (s: Scope, type: string, id: string) =>
    [...root(s), 'sales-related', type, id] as const,
  salesRelatedAll: (s: Scope) => [...root(s), 'sales-related'] as const,
  splitPlan: (s: Scope, id: string) => [...root(s), 'split-plan', id] as const,
  splitPlanAll: (s: Scope) => [...root(s), 'split-plan'] as const,
  salesCampaigns: (s: Scope) => [...root(s), 'sales-campaigns'] as const,
  salesTemplates: (s: Scope) => [...root(s), 'sales-templates'] as const,
  salesBlocks: (s: Scope) => [...root(s), 'sales-blocks'] as const,
  feed: (s: Scope, recordId: string, filter: string) =>
    [...root(s), 'feed', recordId, filter] as const,
  feedAll: (s: Scope) => [...root(s), 'feed'] as const,
  contractSettings: (s: Scope) => [...root(s), 'contract-settings'] as const,
  contractRevisions: (s: Scope, id: string) => [...root(s), 'contract-revisions', id] as const,
  contractTemplates: (s: Scope) => [...root(s), 'contract-templates'] as const,
  contractDocuments: (s: Scope, id: string) => [...root(s), 'contract-documents', id] as const,
  workCalendar: (s: Scope) => [...root(s), 'work-calendar'] as const,
  // v3.1 A1: Şirketim (tenant ayarları)
  companySettings: (s: Scope) => [...root(s), 'company-settings'] as const,
  // M3 herkese açık uçlar
  leadForms: (s: Scope) => [...root(s), 'lead-forms'] as const,
  leadFormMetrics: (s: Scope, id: string) => [...root(s), 'lead-form-metrics', id] as const,
  // v3.1 A5′: kimlik eşleştirme kuyruğu, aday birleştirme önizlemesi, geri doldurma durumu
  identityReviews: (s: Scope, status: string) => [...root(s), 'identity-reviews', status] as const,
  identityReviewsAll: (s: Scope) => [...root(s), 'identity-reviews'] as const,
  leadMergePreview: (s: Scope, source: string, target: string) =>
    [...root(s), 'lead-merge-preview', source, target] as const,
  identityBackfill: (s: Scope) => [...root(s), 'identity-backfill'] as const,
  proposalWeblinkSettings: (s: Scope) => [...root(s), 'proposal-weblink-settings'] as const,
  proposalWeblink: (s: Scope, id: string) => [...root(s), 'proposal-weblink', id] as const,
  // M5 WP 5.4: e-posta (SMTP ayarı, şablonlar, gönderim kaydı, uygunluk, teklif e-postası)
  email: (s: Scope) => [...root(s), 'email'] as const,
  emailSmtp: (s: Scope) => [...root(s), 'email', 'smtp'] as const,
  emailTemplates: (s: Scope) => [...root(s), 'email', 'templates'] as const,
  emailSends: (s: Scope, f: Record<string, string>) => [...root(s), 'email', 'sends', f] as const,
  emailSend: (s: Scope, id: string) => [...root(s), 'email', 'send', id] as const,
  emailAvailability: (s: Scope) => [...root(s), 'email', 'availability'] as const,
  proposalEmail: (s: Scope, id: string) => [...root(s), 'email', 'proposal', id] as const,
  // M5 WP 5.6: gelen e-posta (IMAP hesapları, iletiler, karantina)
  inbound: (s: Scope) => [...root(s), 'inbound'] as const,
  inboundAccounts: (s: Scope) => [...root(s), 'inbound', 'accounts'] as const,
  inboundMessages: (s: Scope, f: Record<string, string>) =>
    [...root(s), 'inbound', 'messages', f] as const,
  inboundMessage: (s: Scope, id: string) => [...root(s), 'inbound', 'message', id] as const,
  inboundRecordAttachments: (s: Scope, type: string, id: string) =>
    [...root(s), 'inbound', 'record-attachments', type, id] as const,
  demoStatus: (s: Scope) => [...root(s), 'demo-status'] as const,
  // v3.1 B3: hesap durumu (deneme / askı)
  accountStatus: (s: Scope) => [...root(s), 'account-status'] as const,
  // M3 K-M3-16: satış görünümleri (pano, tahmin, takım, aday panosu, sipariş kuşbakışı) ve satış seçenek listeleri
  salesView: (s: Scope, view: string, params: Record<string, string | number | undefined>) =>
    [...root(s), 'sales-view', view, params] as const,
  salesViewsAll: (s: Scope) => [...root(s), 'sales-view'] as const,
  salesLookups: (s: Scope) => [...root(s), 'sales-lookups'] as const,
  // M4 WP 4.5: performans monitörü, hedefler, faaliyet raporu, Excel göstergesi (kişisel panolar kapsam anahtarında)
  performance: (s: Scope, ...parts: ReadonlyArray<string | Record<string, string | undefined>>) =>
    [...root(s), 'performance', ...parts] as const,
  performanceAll: (s: Scope) => [...root(s), 'performance'] as const,
  // M4 WP 4.4: destek (seçenek listeleri, modül ayarları, SLA politikaları, müşteri ataması, KPI panosu)
  supportLookups: (s: Scope) => [...root(s), 'support-lookups'] as const,
  supportSettings: (s: Scope) => [...root(s), 'support-settings'] as const,
  supportPolicies: (s: Scope) => [...root(s), 'support-sla-policies'] as const,
  supportCustomers: (s: Scope) => [...root(s), 'support-sla-customers'] as const,
  supportOverview: (s: Scope, params: Record<string, string>) =>
    [...root(s), 'support-overview', params] as const,
  // M4 K-M4-22: destek talebi web formları (yönetim)
  ticketForms: (s: Scope) => [...root(s), 'ticket-forms'] as const,
  ticketFormMetrics: (s: Scope, id: string) => [...root(s), 'ticket-form-metrics', id] as const,
  // WP 2.6: içe/dışa aktarma (iş görünümleri kullanıcıya özeldir; kapsam anahtarında)
  importFields: (s: Scope, type: string) => [...root(s), 'import-fields', type] as const,
  importJob: (s: Scope, id: string) => [...root(s), 'import-job', id] as const,
  importRows: (s: Scope, id: string, run: string) => [...root(s), 'import-rows', id, run] as const,
  imports: (s: Scope) => [...root(s), 'imports'] as const,
  exports: (s: Scope) => [...root(s), 'exports'] as const,
  // WP 2.11: arama (sorgu, tipler, etiket süzgeci, sayfa boyu, cursor/sayfa kipi anahtarda; kullanıcılar arası paylaşım yok)
  search: (
    s: Scope,
    k: {
      q: string;
      types?: readonly string[] | undefined;
      tagIds?: readonly string[] | undefined;
      limit?: number | undefined;
      cursor: string | null;
    },
  ) => [...root(s), 'search', k] as const,
};

/**
 * Kapsam değişince (çıkış, başka kullanıcı/tenant) bütün bellek önbelleğini temizler. App düzeyinde bir kez.
 * Oturumsuzdan (`anon`) kullanıcıya geçişte temizlenmez: önbellekte kullanıcı verisi yoktur (tüm veri sorguları kapsam
 * anahtarlıdır ve kapsam yokken kapalıdır) ve temizlik, sayfa doğrudan yüklendiğinde aynı işlemede başlayan ilk
 * sorguları sessizce iptal edip "yükleniyor" durumunda bırakıyordu (yalnız üretim derlemesinde; WP 2.9'da bulundu).
 */
export function shouldClearOnScopeChange(prev: string | null, next: string): boolean {
  return prev !== null && prev !== next && prev !== scopeKey(null);
}

export function useScopeGuard(): void {
  const scope = useScope();
  const key = scopeKey(scope);
  const qc = useQueryClient();
  const prev = useRef<string | null>(null);
  useEffect(() => {
    if (shouldClearOnScopeChange(prev.current, key)) {
      qc.clear();
      clearMetadataMemory();
    }
    prev.current = key;
  }, [key, qc]);
}
